import React, { useEffect, useState } from "react";
import LayoutNav from "containers/LayoutNav/index";
import { connect, useDispatch, useSelector } from "react-redux";

import { resetLayoutsToEmpty } from "store/actions/layout";
import { updateSidePanelAction } from "store/actions/sidepanel";
import { setPageLayouts } from "store/thunk/layout";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";
import useParamsHandler from "hooks/useLayoutHandler";

import { addWidgetApi, getBaselineList, getReport, getWidgetApi } from "api/report.js";

import getLogger from "utils/logger.util";
import { extractSystemId, getLayout, sortWidgetsBasedOnOrder } from "utils/utils.js";
import { ReportTitleStrings } from "constants/constantStrings";
import { SYSTEMS_PAGES } from "constants/pages.constant";
import { NORMALIZED_FILTERS, NormalizedFiltersStrings } from "constants/report.constant";
import { WIDGET_TYPES } from "constants/widgets.type.const";

import PageLoading, { OverlayLoader } from "components/PageLoading";

import ReportList from "./ReportList";

import "./index.scss";

const Report = props => {
  const Logger = getLogger("Report");
  const dispatch = useDispatch();
  const { getCurrentLayout } = useParamsHandler();
  const { isReportPanelOpened } = useSelector(state => state.sidePanelData);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isDownloading] = useState(false);
  const [widgets, setWidgets] = useState([]);
  const [isWidgetEditing, setIsWidgetEditing] = useState(false);
  const [autogenerate, setAutogenerate] = useState({
    enabled: false,
    interval: "",
    savedInterval: "",
    dayInputValue: "",
    day: "",
    savedDay: "",
    hourInputValue: "",
    hour: "",
    savedHour: ""
  });
  const currentDate = new Date();
  const currentDateString = currentDate.toString();
  const pastDate = new Date();
  const getLastWeekDate = currentDate.getDate() - 7;
  pastDate.setDate(getLastWeekDate);
  const lastWeekString = pastDate.toString();

  const [downloadDateRange, setDownloadDateRange] = useState({
    dateRange: "lastWeek",
    startDateDisplay: lastWeekString.substring(4, 15),
    endDateDisplay: currentDateString.substring(4, 15),
    startDateCustom: null,
    endDateCustom: null
  });
  const [isRerendering, setIsRerendering] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [selectedStartEndDates] = useState({ startDate: null, endDate: null });
  const [isAddLayoutFormOpen, setIsAddLayoutFormOpen] = useState(false);
  const [isEditLayoutFormOpen, setIsEditLayoutFormOpen] = useState(false);
  const [isAddWidgetFormOpen, setIsAddWidgetFormOpen] = useState(props.isLeftSideNavOpen);
  const [isIntialDataFetched, setIsIntialDataFetched] = useState(false);
  const [currentLayout, setCurrentLayout] = useState(null);
  const [widgetAdded, setWidgetAdded] = useState(null);
  const [baselineList, setBaselineList] = useState([]);

  const page = SYSTEMS_PAGES.REPORTS;
  const systemID = extractSystemId();
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    Object.values(NORMALIZED_FILTERS).forEach(tagId => {
      const unit = props.tagsAsObj[tagId]?.Units;
      NormalizedFiltersStrings[tagId] = ReportTitleStrings[tagId].replace("%", unit);
    });
    return () => {
      dispatch(resetLayoutsToEmpty());
    };
  }, []);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsRerendering(true);
        const response = await getReport();
        const { Layouts } = response.data;
        if (Layouts.length) {
          const { defaultLayout, layouts } = getCurrentLayout(Layouts);
          props.onSetPageLayouts(layouts, defaultLayout, page);
          setIsIntialDataFetched(true);
        } else {
          setIsLoaded(true);
          setIsRerendering(false);
        }
      } catch (err) {
        Logger.error("Error whilte fetchInitialData: ", err);
      }
    };
    if (!isIntialDataFetched) fetchInitialData();
  }, [isIntialDataFetched]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const activeLayout = getLayout(props.currentLayout);
    if (activeLayout && Object.keys(activeLayout).length && (currentLayout === null || currentLayout !== activeLayout.ID)) {
      setIsRerendering(true);
      setCurrentLayout(activeLayout.ID);
      fetchData(activeLayout);
    } else {
      setIsRerendering(false);
    }
  }, [isRerendering, props.currentLayout]);

  const fetchBaselines = async () => {
    try {
      const response = await getBaselineList();
      if (response.status >= 200 && response.status < 300) {
        const BaselineList = response.data;
        setBaselineList(BaselineList);
      }
    } catch (err) {
      Logger.error("fetchBaselines - error: ", err);
    }
  };

  const fetchData = async currentLayout => {
    try {
      const response = await getWidgetApi(currentLayout);
      const { Widgets } = response.data;
      if (Widgets.length) {
        const updatedWidgets = Widgets.map(v => ({ ...v, isToUpdatePreview: true, isUpdating: false }));
        setWidgets(sortWidgetsBasedOnOrder(updatedWidgets));
        fetchBaselines();
      } else setWidgets([]);
    } catch (err) {
      Logger.error("Fetch Report data error: ", err);
    } finally {
      setIsRerendering(false);
      setIsLoaded(true);
    }
  };

  const onWidgetAction = (actionType, payload) => {
    switch (actionType) {
      case "ADD_WIDGET": {
        let order = 0;
        const widgetLength = widgets?.length;
        if (widgets && widgetLength > 0) {
          order = widgets[widgetLength - 1].Order ? widgets[widgetLength - 1].Order + 1 : widgets.length;
        }
        payload = { ...payload, Order: order };
        addReportWidget(payload);
        break;
      }
    }
  };

  const addReportWidget = async payload => {
    // setIsRerendering(true);
    objAppInsights.trackEvent("addWidgetInReports");
    try {
      setIsUpdating(true);
      const respData = await addWidgetApi(payload);
      setWidgetAdded(respData);
      if (payload.Type === WIDGET_TYPES.NORMALIZATION && !baselineList.length) {
        fetchBaselines();
      }
    } catch (err) {
      Logger.log("error on add Wiget " + err);
    } finally {
      setIsUpdating(false);
    }
  };

  useEffect(() => {
    if (selectedStartEndDates.startDate !== null) {
      const calendarStartDate = selectedStartEndDates.startDate._d.toString();
      setDownloadDateRange({
        ...downloadDateRange,
        dateRange: "custom",
        startDateDisplay: calendarStartDate.substring(4, 15),
        startDateCustom: selectedStartEndDates.startDate.unix()
      });
    }
    if (selectedStartEndDates.endDate !== null) {
      const calendarEndDate = selectedStartEndDates.endDate._d.toString();
      setDownloadDateRange({
        ...downloadDateRange,
        dateRange: "custom",
        endDateDisplay: calendarEndDate.substring(4, 15),
        endDateCustom: selectedStartEndDates.endDate.unix()
      });
    }
  }, [selectedStartEndDates]);

  const onToggleCollapsedNav = () => {
    const isSidePanelOpened = !isReportPanelOpened && !isWidgetEditing;
    dispatch(updateSidePanelAction({ isReportPanelOpened: isSidePanelOpened }));
    setIsWidgetEditing(false);
    setIsAddLayoutFormOpen(false);
    setIsAddWidgetFormOpen(false);
    setIsEditLayoutFormOpen(false);
  };

  const editReportWidget = isEditing => setIsWidgetEditing(isEditing);

  const isLoadingView = () => !isLoaded || isDownloading;

  return (
    <div className="sideNav">
      {isLoadingView() ? (
        <PageLoading />
      ) : (
        <div>
          <div className="sideNavMainContainer">
            {(isRerendering || props.showOverlayLoader) && <OverlayLoader />}
            <div
              className={`sideNavMenubutton ${
                isReportPanelOpened || isWidgetEditing ? "sideNavOpenMenuwidth sideNavCloseMenuButton" : "sideNavCloseMenuwidth sideNavOpenMenuButton"
              }`}
              onClick={onToggleCollapsedNav}
            />
            {isReportPanelOpened && (
              <div className="sideNavMenuOpen">
                <LayoutNav
                  page={page}
                  systemID={systemID}
                  className="sideNavMenuContainer"
                  setIsReportRerendering={setIsRerendering}
                  tagsList={props.tagsList}
                  isLeftSideNavOpen={isReportPanelOpened}
                  setIsAddLayoutFormOpen={setIsAddLayoutFormOpen}
                  isAddLayoutFormOpen={isAddLayoutFormOpen}
                  setIsEditLayoutFormOpen={setIsEditLayoutFormOpen}
                  isEditLayoutFormOpen={isEditLayoutFormOpen}
                  setIsAddWidgetFormOpen={setIsAddWidgetFormOpen}
                  isAddWidgetFormOpen={isAddWidgetFormOpen}
                  autogenerate={autogenerate}
                  setAutogenerate={setAutogenerate}
                  onWidgetAction={onWidgetAction}
                />
              </div>
            )}
            <div className={`sideNavContainer ${isWidgetEditing && !isReportPanelOpened ? "side-menu-opened" : null}`}>
              <div className="width100 report-list-main-container">
                {
                  <ReportList
                    widgets={widgets}
                    setWidgets={setWidgets}
                    baselineList={baselineList}
                    tagsList={props.tagsList}
                    systemID={systemID}
                    page={page}
                    isWidgetEditing={isWidgetEditing}
                    editReportWidget={editReportWidget}
                    currentLayout={props.currentLayout}
                    autogenerate={props}
                    setWidgetAdded={setWidgetAdded}
                    widgetAdded={widgetAdded}
                    setIsUpdating={setIsUpdating}
                    isUpdating={isUpdating}
                    isReportPanelOpened={isReportPanelOpened}
                    setIsAddWidgetFormOpen={setIsAddWidgetFormOpen}
                  />
                }
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ tags, layout, poll }) => ({
  tagsList: tags.tags,
  tagsAsObj: tags.tagsAsObj,
  currentLayout: layout.currentLayouts,
  systemsStatus: poll.systemsStatus,
  showOverlayLoader: layout.showShowOverlayLoader
});

const mapDispatchToProps = dispatch => ({
  onSetPageLayouts: (layouts, newLayout, page) => dispatch(setPageLayouts(layouts, newLayout, page))
});

export default connect(mapStateToProps, mapDispatchToProps)(Report);
